/**
 * AssigneePicker.jsx (admins): who a to-do is for, or (with `everyone`) the
 * Team view's filter, whose first option is "Everyone" (value '').
 *
 * People come from GET /todos/people: accounts that can sign in, never
 * external users or the test login, already in role order. A current assignee
 * who is no longer on that list still shows by name (`fallback`).
 */
import { useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography, Chip } from '@mui/material';
import { useTodoPeople } from '../../hooks/useTodos';

const EVERYONE = { id: '', name: 'Everyone', role: '' };

const ROLE_LABEL = {
  super_admin: 'Super',
  admin: 'Admin',
  core_crew: 'Core',
  crew: 'Crew',
};

const ROLE_COLOR = {
  super_admin: '#D2691E',
  admin: '#7C3AED',
  core_crew: '#0EA5E9',
  crew: '#64748B',
};

export default function AssigneePicker({
  value, onChange, everyone = false, fallback, meId, label = 'For', disabled, trackKey = 'todos.assignee', sx,
}) {
  const { data: people = [], isPending, isError } = useTodoPeople();

  const options = useMemo(() => {
    const list = [...(everyone ? [EVERYONE] : []), ...people];
    if (value && fallback?.id === value && !list.some((p) => p.id === value)) {
      list.push({ id: fallback.id, name: fallback.name || 'Someone', role: '' });
    }
    return list;
  }, [people, everyone, value, fallback]);

  const selected = options.find((o) => o.id === (value || '')) || (everyone ? EVERYONE : null);

  return (
    <Autocomplete
      data-track={trackKey}
      size="small"
      disableClearable
      disabled={disabled}
      options={options}
      value={selected}
      loading={isPending}
      onChange={(_e, opt) => onChange(opt?.id || '')}
      getOptionLabel={(opt) => (opt?.id && opt.id === meId ? `${opt.name} (you)` : opt?.name || '')}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      sx={{ minWidth: { xs: '100%', sm: 220 }, '& .MuiInputBase-root': { minHeight: 44 }, ...sx }}
      renderOption={(props, opt) => {
        const { key: _key, ...rest } = props;
        return (
          <Box component="li" key={opt.id || 'everyone'} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 44 }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
              {opt.id && opt.id === meId ? `${opt.name} (you)` : opt.name}
            </Typography>
            {ROLE_LABEL[opt.role] && (
              <Chip
                size="small"
                label={ROLE_LABEL[opt.role]}
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  bgcolor: `${ROLE_COLOR[opt.role]}25`,
                  color: ROLE_COLOR[opt.role],
                  border: `1px solid ${ROLE_COLOR[opt.role]}40`,
                }}
              />
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={isError ? 'Could not load people.' : undefined}
          error={isError}
        />
      )}
    />
  );
}
